import fetch from 'node-fetch';
import utils from "../../universal/utils";

// Action Types
export const DASHBOARD_LOADING = 'DASHBOARD_LOADING';
export const DASHBOARD_LOADED = 'DASHBOARD_LOADED';
export const DASHBOARD_ERROR = 'DASHBOARD_ERROR';
export const SET_DASHBOARD_OVERVIEW = 'SET_DASHBOARD_OVERVIEW';
export const UPDATE_REALTIME_STATS = 'UPDATE_REALTIME_STATS';
export const UPDATE_AGENT_STATUS = 'UPDATE_AGENT_STATUS';
export const UPDATE_MATCH_STATUS = 'UPDATE_MATCH_STATUS';
export const SET_SELECTED_AGENT = 'SET_SELECTED_AGENT';
export const SET_SELECTED_MATCH = 'SET_SELECTED_MATCH';

const basePath = utils.getEdgeBasePath();

function fetchAdmin(url, options = {}) {
  return fetch(url, {
    ...options,
    credentials: "include",
  });
}

async function parseAdminResponse(response) {
  let data = {};

  try {
    data = await response.json();
  } catch (error) {
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    throw error;
  }

  if (!response.ok || data?.ok === false) {
    throw new Error(
      data?.error ||
        data?.err ||
        data?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }

  return data;
}

// Fetch dashboard overview
export const fetchDashboardOverview = ({ userId }) => {
  return async (dispatch) => {
    if (!userId) return;
    dispatch({ type: DASHBOARD_LOADING });
    
    try {
      const response = await fetchAdmin(
        `${basePath}/api/v7/admin/dashboard/overview/adpass/${userId}`
      );
      const data = await parseAdminResponse(response);
      
      if (data.ok) {
        dispatch({
          type: DASHBOARD_LOADED,
          payload: data.dashboard
        });
      } else {
        throw new Error(data.error || 'Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      dispatch({
        type: DASHBOARD_ERROR,
        payload: error.message
      });
    }
  };
};

// Set dashboard overview directly (e.g., from WebSocket push)
export const setDashboardOverview = (overview) => ({
  type: SET_DASHBOARD_OVERVIEW,
  payload: overview
});

// Fetch agent details
export const fetchAgentDetails = ({ agentIp, userId }) => {
  return async (dispatch) => {
    if (!agentIp || !userId) return;
    try {
      const response = await fetchAdmin(
        `${basePath}/api/v7/admin/agent/${agentIp}/adpass/${userId}`
      );
      const data = await parseAdminResponse(response);
      
      if (data.ok) {
        dispatch({
          type: SET_SELECTED_AGENT,
          payload: data.agent
        });
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
    }
  };
};

// Fetch match details
export const fetchMatchDetails = ({ matchId, userId }) => {
  return async (dispatch) => {
    if (!matchId || !userId) return;
    try {
      const response = await fetchAdmin(
        `${basePath}/api/v7/admin/match/${matchId}/details/adpass/${userId}`
      );
      const data = await parseAdminResponse(response);
      
      if (data.ok) {
        dispatch({
          type: SET_SELECTED_MATCH,
          payload: data.match
        });
      }
    } catch (error) {
      console.error('Error fetching match details:', error);
    }
  };
};

// Export match debug data
export const exportMatchDebugData = ({ matchId, userId }) => {
  return async () => {
    if (!matchId || !userId) return;
    try {
      const response = await fetchAdmin(
        `${basePath}/api/v7/admin/match/${matchId}/export/adpass/${userId}`
      );
      const data = await parseAdminResponse(response);
      
      // Trigger download
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `match-${matchId}-debug.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      return data;
    } catch (error) {
      console.error('Error exporting debug data:', error);
      throw error;
    }
  };
};

// Search matches
export const searchMatches = ({ filters, userId }) => {
  return async (dispatch) => {
    if (!userId) return [];
    try {
      const response = await fetchAdmin(
        `${basePath}/api/v7/admin/matches/search/adpass/${userId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(filters || {})
        }
      );
      const data = await parseAdminResponse(response);
      
      if (data.ok) {
        return data.matches;
      }
    } catch (error) {
      console.error('Error searching matches:', error);
      return [];
    }
  };
};

// Update real-time stats (from WebSocket)
export const updateRealtimeStats = (stats) => ({
  type: UPDATE_REALTIME_STATS,
  payload: stats
});

// Update agent status (from WebSocket)
export const updateAgentStatus = (agentData) => ({
  type: UPDATE_AGENT_STATUS,
  payload: agentData
});

// Update match status (from WebSocket)
export const updateMatchStatus = (matchData) => ({
  type: UPDATE_MATCH_STATUS,
  payload: matchData
});

